import { supabase } from './supabase';

// ---------------------------------------------------------------------------
// VAPID public key
// ---------------------------------------------------------------------------
// We fetch from the DB (apro_vapid_config) to guarantee it matches the private
// key the server uses when sending. Falling back to the env var only if the
// DB read fails (e.g. no RLS policy yet, network error, etc.).
// ---------------------------------------------------------------------------

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('apro_vapid_config')
      .select('public_key')
      .limit(1)
      .maybeSingle();

    if (!error && data?.public_key) {
      return data.public_key;
    }
  } catch {
    // fall through to env-var fallback
  }
  // Fallback: env var (requires VITE_VAPID_PUBLIC_KEY to match the DB value)
  const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  return envKey || null;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

// ---------------------------------------------------------------------------
// Service Worker
// ---------------------------------------------------------------------------

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service Workers not supported');
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    console.log('[Push] Service Worker registered');
    return registration;
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Permission
// ---------------------------------------------------------------------------

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[Push] Notifications not supported');
    return 'denied';
  }
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission !== 'denied') {
    return await Notification.requestPermission();
  }
  return Notification.permission;
}

// ---------------------------------------------------------------------------
// Internal: clear an existing browser subscription and its DB record
// ---------------------------------------------------------------------------

async function clearExistingSubscription(
  registration: ServiceWorkerRegistration,
  userId: string,
): Promise<void> {
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return;

  // Deactivate in DB
  await supabase
    .from('apro_push_subscriptions')
    .update({ is_active: false })
    .eq('endpoint', existing.endpoint)
    .eq('user_id', userId);

  // Remove from browser
  await existing.unsubscribe();
  console.log('[Push] Cleared stale subscription');
}

// ---------------------------------------------------------------------------
// Internal: save (upsert) a fresh subscription to the DB
// ---------------------------------------------------------------------------

async function saveSubscription(
  subscription: PushSubscription,
  userId: string,
): Promise<void> {
  const json = subscription.toJSON();
  const { error } = await supabase
    .from('apro_push_subscriptions')
    .upsert(
      {
        user_id: userId,
        endpoint: subscription.endpoint,
        p256dh_key: json.keys?.p256dh ?? '',
        auth_key: json.keys?.auth ?? '',
        user_agent: navigator.userAgent,
        is_active: true,
      },
      { onConflict: 'endpoint' },
    );
  if (error) throw error;
  console.log('[Push] Subscription saved');
}

// ---------------------------------------------------------------------------
// Subscribe
// Always creates a FRESH subscription so the VAPID key is always in sync.
// If a stale subscription exists it is removed first.
// ---------------------------------------------------------------------------

export async function subscribeToPushNotifications(): Promise<boolean> {
  try {
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      throw new Error(
        'No se encontró la clave VAPID pública. ' +
        'Asegúrate de que existe una fila en apro_vapid_config ' +
        'o configura VITE_VAPID_PUBLIC_KEY en tu .env.',
      );
    }

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('[Push] Permission denied');
      return false;
    }

    const registration = await registerServiceWorker();
    if (!registration) return false;

    await navigator.serviceWorker.ready;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    // Always clear any existing subscription so the key is never stale.
    await clearExistingSubscription(registration, user.id);

    // Create a fresh subscription with the current VAPID key.
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    await saveSubscription(subscription, user.id);
    return true;
  } catch (error) {
    console.error('[Push] Error subscribing:', error);
    throw error; // re-throw so the UI can show the message
  }
}

// ---------------------------------------------------------------------------
// Unsubscribe
// ---------------------------------------------------------------------------

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) return true;

    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('apro_push_subscriptions')
        .update({ is_active: false })
        .eq('endpoint', subscription.endpoint)
        .eq('user_id', user.id);
    }

    await subscription.unsubscribe();
    return true;
  } catch (error) {
    console.error('[Push] Error unsubscribing:', error);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

export async function isPushNotificationSupported(): Promise<boolean> {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function isPushNotificationSubscribed(): Promise<boolean> {
  try {
    if (!await isPushNotificationSupported()) return false;
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) return false;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('[Push] Error checking subscription status:', error);
    return false;
  }
}

export function getNotificationPermissionStatus(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

// ---------------------------------------------------------------------------
// Listen for subscription changes (browser rotates keys)
// ---------------------------------------------------------------------------

export function listenForSubscriptionChanges(): void {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('message', async (event) => {
    if (event.data?.type !== 'PUSH_SUBSCRIPTION_CHANGED') return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const sub = event.data.subscription;
      await supabase
        .from('apro_push_subscriptions')
        .upsert(
          {
            user_id: user.id,
            endpoint: sub.endpoint,
            p256dh_key: sub.p256dh_key,
            auth_key: sub.auth_key,
            user_agent: navigator.userAgent,
            is_active: true,
          },
          { onConflict: 'endpoint' },
        );
    } catch (error) {
      console.error('[Push] Error updating changed subscription:', error);
    }
  });
}
